import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Btn } from "@/components/kit";
import { X, Ruler, RotateCcw, Check, Loader2, MousePointerClick, Search, MapPin } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const PITCH_FACTOR = { "3/12": 1.031, "4/12": 1.054, "5/12": 1.083, "6/12": 1.118,
  "7/12": 1.158, "8/12": 1.202, "9/12": 1.25, "10/12": 1.302, "12/12": 1.414 };

function polygonAreaSqFt(points) {
  if (points.length < 3) return 0;
  const R = 6378137, toRad = (d) => (d * Math.PI) / 180;
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i], p2 = points[(i + 1) % points.length];
    a += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs((a * R * R) / 2) * 10.7639; // m² → ft²
}

export default function MeasureMap({ address, onClose, onDone }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const polyRef = useRef(null);
  const refMarkerRef = useRef(null);
  const ptsRef = useRef([]);
  const [n, setN] = useState(0);
  const [pitch, setPitch] = useState("6/12");
  const [locating, setLocating] = useState(true);
  const [query, setQuery] = useState(address || "");
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [tracing, setTracing] = useState(false);
  const tracingRef = useRef(false);

  const redraw = () => {
    const map = mapRef.current;
    if (polyRef.current) { map.removeLayer(polyRef.current); polyRef.current = null; }
    if (ptsRef.current.length >= 2)
      polyRef.current = L.polygon(ptsRef.current, { color: "#C8102E", weight: 2, fillColor: "#C8102E", fillOpacity: 0.2 }).addTo(map);
  };

  const geocode = async (q) => {
    if (!q || !q.trim()) return;
    setSearching(true); setNotFound(false);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
      const d = await r.json();
      if (d && d[0]) moveTo(+d[0].lat, +d[0].lon);
      else setNotFound(true);
    } catch { setNotFound(true); }
    finally { setSearching(false); }
  };

  const moveTo = (lat, lng) => {
    setNotFound(false);
    mapRef.current.setView([lat, lng], 20);
    if (refMarkerRef.current) mapRef.current.removeLayer(refMarkerRef.current);
    refMarkerRef.current = L.marker([lat, lng], {
      icon: L.divIcon({ className: "", html: '<div style="width:14px;height:14px;border-radius:50%;background:#1e3a5f;border:3px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,.3)"></div>', iconSize: [14, 14], iconAnchor: [7, 7] }),
    }).addTo(mapRef.current);
  };

  useEffect(() => {
    const map = L.map(containerRef.current, { zoomControl: true }).setView([32.7767, -96.797], 19);
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 22, attribution: "Imagery © Esri" }).addTo(map);
    groupRef.current = L.featureGroup().addTo(map);
    map.on("click", (e) => {
      if (!tracingRef.current) return;
      ptsRef.current.push(e.latlng);
      L.circleMarker(e.latlng, { radius: 5, color: "#fff", weight: 2, fillColor: "#C8102E", fillOpacity: 1 }).addTo(groupRef.current);
      redraw(); setN((c) => c + 1);
    });
    mapRef.current = map;
    if (address) geocode(address).finally(() => setLocating(false));
    else setLocating(false);
    setTimeout(() => map.invalidateSize(), 200);
    return () => map.remove();
  }, []); // eslint-disable-line

  const reset = () => { ptsRef.current = []; groupRef.current?.clearLayers(); if (polyRef.current) { mapRef.current.removeLayer(polyRef.current); polyRef.current = null; } setN(0); };
  const startTracing = () => { tracingRef.current = true; setTracing(true); };

  const areaFt = polygonAreaSqFt(ptsRef.current);
  const squares = +((areaFt * (PITCH_FACTOR[pitch] || 1.118)) / 100).toFixed(1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60" onClick={onClose}>
      <div className="mb-card p-4 sm:p-5 w-full max-w-3xl animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="measure-map-modal">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-display text-2xl font-bold uppercase text-navy flex items-center gap-2"><Ruler className="w-5 h-5" /> Measure on Satellite</h3>
          <button onClick={onClose} data-testid="measure-close-btn"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form className="flex items-center gap-2 mb-2" onSubmit={(e) => { e.preventDefault(); geocode(query); }}>
          <div className="flex-1">
            <AddressAutocomplete testid="measure-address-input" value={query}
              onChange={(v) => { setQuery(v); setNotFound(false); }}
              onSelect={(p) => moveTo(p.lat, p.lon)}
              placeholder="Search an address to jump the map there…"
              className="w-full border-2 border-slate-200 rounded-lg pl-3 pr-3 py-2 text-sm outline-none focus:border-navy" />
          </div>
          <Btn variant="red" type="submit" disabled={searching || !query.trim()} testid="measure-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
          </Btn>
        </form>
        {notFound && <div className="text-xs text-brandred font-semibold mb-2" data-testid="measure-notfound">Couldn't find that address — try adding city/state or a nearby landmark.</div>}
        {tracing ? (
          <div className="text-xs text-slate-500 mb-2 flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /> Tracing on — click each corner of the roof. Free Esri satellite imagery · area × pitch.</div>
        ) : (
          <div className="flex items-center justify-between gap-2 mb-2 rounded-lg bg-navy/5 border border-navy/10 px-3 py-2">
            <span className="text-xs text-navy font-semibold flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-brandred" /> Verify the pinned location, pan/zoom to the roof, then start tracing.</span>
            <Btn variant="red" onClick={startTracing} testid="measure-start-trace-btn"><MousePointerClick className="w-4 h-4" /> Start Tracing</Btn>
          </div>
        )}
        <div className="relative rounded-xl overflow-hidden border-2 border-slate-200">
          <div ref={containerRef} data-testid="measure-map" style={{ height: "48vh", minHeight: 320, width: "100%" }} />
          {locating && <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-navy"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Finding the property…</div>}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="mb-card px-4 py-2 border">
            <div className="text-[10px] uppercase text-slate-400 font-semibold">Roof Area</div>
            <div className="font-num font-bold text-navy">{areaFt ? `${Math.round(areaFt).toLocaleString()} ft²` : "—"}</div>
          </div>
          <div className="mb-card px-4 py-2 border">
            <div className="text-[10px] uppercase text-slate-400 font-semibold">Squares</div>
            <div className="font-num font-bold text-brandred" data-testid="measure-squares">{squares || "—"}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-400 font-semibold block">Pitch</label>
            <select value={pitch} onChange={(e) => setPitch(e.target.value)} data-testid="measure-pitch"
              className="border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm font-num outline-none focus:border-navy">
              {Object.keys(PITCH_FACTOR).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex-1" />
          <Btn variant="ghost" onClick={reset} testid="measure-reset-btn"><RotateCcw className="w-4 h-4" /> Reset</Btn>
          <Btn variant="red" onClick={() => onDone(squares, pitch)} disabled={squares <= 0} testid="measure-use-btn"><Check className="w-4 h-4" /> Use {squares || 0} Squares</Btn>
        </div>
      </div>
    </div>
  );
}
